<script setup lang="ts">
import { getHostname } from '@@/bits/url'
import type { AudioSearchResult, VideoSearchResult } from '@@/bits/web_search'
import { SearchFilters } from '@@/bits/web_search'
import FallbackAudio from '@@/images/fallback_audio.png'
import ImageThumbnail from '@@/library/v4/components/ImageThumbnail.vue'
import { nextTick, onMounted, ref, watch } from 'vue'

const props = defineProps<{
  results: (AudioSearchResult | VideoSearchResult)[]
  filter: SearchFilters
}>()

const emit = defineEmits<{
  (name: 'select', url: string): void
}>()

const LOADING_IMAGES_BG_COLOR = ['red', 'blue', 'green', 'yellow', 'purple', 'pink', 'cyan', 'orange']
const CROP_ASPECT_RATIO = 1.8 /* 16.2:9 to remove borders nicely since 16/9=1.(7) is non-terminating */
const DEFAULT_THUMBNAIL_HEIGHT = 768
const DEFAULT_THUMBNAIL_WIDTH = DEFAULT_THUMBNAIL_HEIGHT * CROP_ASPECT_RATIO

const container = ref<HTMLUListElement>()
const imageWrappers = ref<HTMLLIElement[]>()

const imageWidth = ref(0)

const handleResultsUpdate = async (): Promise<void> => {
  await nextTick()
  imageWidth.value = imageWrappers.value && imageWrappers.value[0] ? imageWrappers.value[0].clientWidth : 0
}

const select = (url: string): void => {
  emit('select', url)
}

const audioDescription = (result: AudioSearchResult): string => {
  if (result.release_date) {
    // podcast episode
    const showName = result.show_name
    if (showName) {
      return showName + ' · ' + new Date(result.release_date).toLocaleDateString()
    }
    let shortenedDescription = result.description.split(' ').slice(0, 15).join(' ')
    if (shortenedDescription.length < result.description.length) {
      shortenedDescription = shortenedDescription + '...'
    }
    return new Date(result.release_date).toLocaleDateString() + ' · ' + shortenedDescription
  }
  return result.description
}

const providerName = (result: VideoSearchResult): string => {
  if (result.provider_name) return result.provider_name
  return getHostname(result.url)
}

const isUsingFallbackThumbnail = (result: AudioSearchResult): boolean => {
  return !result.thumbnail_url || result.thumbnail_url === ''
}

watch(() => props.results, handleResultsUpdate)

onMounted((): void => {
  imageWidth.value = (container.value?.clientWidth ?? 0) / 3 - 12
  // After the components are auto-layouted, we assigned the fixed imageWidth so that
  // Beethoven-Vivaldi can load the most size-optimal image
  handleResultsUpdate()
})
</script>

<template>
  <ul
    ref="container"
    :class="['w-full', 'grid', 'grid-cols-2', 'md:grid-cols-3', 'mb-2', 'mt-2', 'mx-0', 'gap-4', 'pt-1']"
  >
    <li
      v-for="(result, index) in results"
      ref="imageWrappers"
      :key="filter + '-result-' + index"
      :class="['p-1/2', 'flex', 'flex-col']"
    >
      <div
        :class="[
          'rounded-2xl',
          'focus:ring-2',
          'focus:ring-grape-500',
          'dark:focus:ring-canary-500',
          'focus:ring-opacity-100',
          'hover-hover:hover:ring-2',
          'hover-hover:hover:ring-grape-500',
          'hover-hover:dark:hover:ring-canary-500',
          'hover-hover:hover:ring-opacity-100',
          'cursor-pointer',
        ]"
        tabindex="0"
        @click="select(result.url)"
        @keydown.enter="select(result.url)"
        @keydown.space="select(result.url)"
      >
        <div class="relative">
          <ImageThumbnail
            class="rounded-2xl"
            :placeholder-color="LOADING_IMAGES_BG_COLOR[index % 7]"
            :original-image-width="result.thumbnail_width || DEFAULT_THUMBNAIL_WIDTH"
            :original-image-height="result.thumbnail_height || DEFAULT_THUMBNAIL_HEIGHT"
            :title="result.title"
            :src="isUsingFallbackThumbnail(result) ? FallbackAudio : result.thumbnail_url"
            :proxy-image="!isUsingFallbackThumbnail(result)"
            :width="imageWidth"
            :resize-strategy="filter == SearchFilters.Audio ? 'stretch' : 'crop'"
            :aspect-ratio="filter == SearchFilters.Audio ? 1 : CROP_ASPECT_RATIO"
          />
          <div
            v-if="result.duration || filter == SearchFilters.Videos"
            :class="[
              'ss01-nums',
              'text-xs',
              'h-5',
              'rounded-xl',
              'bg-nightfall-600',
              'text-white-100',
              'px-2',
              'items-center',
              'justify-center',
              'flex',
              'font-medium',
              'absolute',
              'bottom-1',
              'right-1',
            ]"
          >
            <span v-if="filter == SearchFilters.Videos && !result.duration" class="yt-live-text uppercase">
              {{ __('Live') }}
            </span>
            <template v-else>
              {{ result.duration }}
            </template>
          </div>
        </div>

        <div class="mx-2 my-2">
          <div class="text-14-19 font-medium text-highway-100 dark:text-white-100 line-clamp-2">{{ result.title }}</div>
          <div
            v-if="filter == SearchFilters.Audio"
            class="mt-1 text-body-extra-small leading-4 text-highway-100 dark:text-white-100"
            :title="result.description"
          >
            {{ audioDescription(result) }}
          </div>
          <div
            v-if="filter == SearchFilters.Videos"
            class="mt-1 text-12-15 text-highway-200 dark:text-white-200 truncate"
          >
            {{ providerName(result) }}
          </div>
        </div>
      </div>
    </li>
  </ul>
</template>

<style lang="scss" scoped>
// Red dot before LIVE text
.yt-live-text {
  &:before {
    display: inline-block;
    width: 5px;
    height: 5px;
    vertical-align: 4%;
    margin-right: 3px;
    background: #f00;
    content: '';
    border-radius: 5px;
  }
}
</style>
